import pdfPackage from 'pdfjs-dist/package.json';
const PDF_VERSION = pdfPackage.version;
export const MATERIAL_HTML_WIDTH = 816;
export const MATERIAL_HTML_STYLE = {boxSizing:'border-box',width:'100%',padding:'40px',fontFamily:'Arial, sans-serif',fontSize:'16px',lineHeight:1.6,color:'#1f2937',background:'#fff',overflowWrap:'anywhere'};
const abortError = () => Object.assign(new Error('Document loading was cancelled.'), {name:'AbortError'});
const checkAbort = signal => {if(signal?.aborted)throw abortError();};
function abortable(promise, signal, cancel) {
    if (!signal) return promise;
    if (signal.aborted) {cancel?.();return Promise.reject(abortError());}
    return new Promise((resolve,reject)=>{
        const abort=()=>{cancel?.();reject(abortError());};
        signal.addEventListener('abort',abort,{once:true});
        Promise.resolve(promise).then(resolve,reject).finally(()=>signal.removeEventListener('abort',abort));
    });
}
function safeUrl(value, image = false) {
    const clean=String(value || '').trim();
    if(!clean)return '';
    if(image && /^data:image\/(?:png|jpe?g|gif|webp|bmp);base64,[a-z0-9+/=\s]+$/i.test(clean))return clean;
    if(!image && (clean.startsWith('#') || /^mailto:[^\s]+$/i.test(clean)))return clean;
    try {
        const url=new URL(clean,window.location.href);
        return ['http:','https:','blob:'].includes(url.protocol)?url.href:'';
    } catch {return '';}
}
const allowedTags=new Set(['P','DIV','SPAN','BR','HR','H1','H2','H3','H4','H5','H6','STRONG','B','EM','I','U','S','STRIKE','SUP','SUB','UL','OL','LI','BLOCKQUOTE','PRE','CODE','TABLE','THEAD','TBODY','TFOOT','TR','TD','TH','CAPTION','COLGROUP','COL','IMG','A']);
const discardedTags=new Set(['SCRIPT','STYLE','LINK','META','IFRAME','OBJECT','EMBED','FORM','INPUT','BUTTON','SELECT','OPTION','TEXTAREA','SVG','MATH','BASE','VIDEO','AUDIO','SOURCE']);
const allowedStyle=new Set(['color','background-color','text-align','vertical-align','font-weight','font-style','font-size','font-family','text-decoration','white-space','width','height','min-width','max-width','border','border-top','border-bottom','border-left','border-right','border-collapse','padding','padding-top','padding-right','padding-bottom','padding-left','margin','margin-top','margin-right','margin-bottom','margin-left']);

// Mammoth and spreadsheet HTML are document data, not trusted application HTML.
export function sanitizeMaterialHtml(html) {
    const parsed=new DOMParser().parseFromString(String(html || ''),'text/html');
    for(const element of [...parsed.body.querySelectorAll('*')]) {
        if(discardedTags.has(element.tagName)) {element.remove();continue;}
        if(!allowedTags.has(element.tagName)) {element.replaceWith(...element.childNodes);continue;}
        const styles=[];
        for(let index=0;index<element.style.length;index++) {
            const property=element.style[index],value=element.style.getPropertyValue(property);
            if(allowedStyle.has(property) && !/url\s*\(|expression\s*\(|javascript:|@import/i.test(value))styles.push(`${property}:${value}`);
        }
        for(const attribute of [...element.attributes]) {
            const name=attribute.name.toLowerCase();
            if(name==='style' || name.startsWith('on') || !['src','href','alt','title','colspan','rowspan','width','height','start'].includes(name))element.removeAttribute(attribute.name);
        }
        if(styles.length)element.setAttribute('style',styles.join(';'));
        if(element.hasAttribute('src')) {
            const src=element.tagName==='IMG'?safeUrl(element.getAttribute('src'),true):'';
            if(src)element.setAttribute('src',src);else element.removeAttribute('src');
        }
        if(element.hasAttribute('href')) {
            const href=element.tagName==='A'?safeUrl(element.getAttribute('href')):'';
            if(href) {element.setAttribute('href',href);element.setAttribute('target','_blank');element.setAttribute('rel','noopener noreferrer');}
            else element.removeAttribute('href');
        }
        for(const attr of ['colspan','rowspan','width','height','start'])if(element.hasAttribute(attr) && !/^\d{1,5}(?:%|px)?$/.test(element.getAttribute(attr)))element.removeAttribute(attr);
        if(element.tagName==='IMG') {element.style.maxWidth='100%';element.style.height='auto';}
        if(element.tagName==='TABLE') {element.style.borderCollapse='collapse';element.style.maxWidth='100%';}
        if(['TD','TH'].includes(element.tagName)) {element.style.border=element.style.border || '1px solid #cbd5e1';element.style.padding=element.style.padding || '6px 8px';}
        if(/^H[1-6]$/.test(element.tagName)) {element.style.fontWeight='bold';element.style.fontSize=({H1:'28px',H2:'24px',H3:'21px',H4:'18px',H5:'17px',H6:'16px'})[element.tagName];element.style.margin='14px 0 8px';}
        if(element.tagName==='P')element.style.margin='0 0 12px';
        if(['UL','OL'].includes(element.tagName)) {element.style.margin='0 0 12px';element.style.paddingLeft='24px';element.style.listStyleType=element.tagName==='UL'?'disc':'decimal';}
    }
    return parsed.body.innerHTML;
}
async function readImage(url,signal) {
    checkAbort(signal);
    const image=new Image();
    const promise=new Promise((resolve,reject)=>{
        image.onload=()=>image.naturalWidth && image.naturalHeight?resolve(image):reject(new Error('This image has no readable content.'));
        image.onerror=()=>reject(new Error('The lecture image could not be loaded. Check your connection or ask your tutor to upload it again.'));
        image.src=url;
    });
    try {return await abortable(promise,signal,()=>{image.src='';});}
    finally {image.onload=null;image.onerror=null;}
}

export async function measureMaterialHtml(page,{signal}={}) {
    checkAbort(signal);
    const node=document.createElement('div');
    Object.assign(node.style,MATERIAL_HTML_STYLE,{position:'absolute',left:'-100000px',top:'0',visibility:'hidden',width:`${page.width || MATERIAL_HTML_WIDTH}px`});
    node.innerHTML=page.html;
    document.body.appendChild(node);
    try {
        if(document.fonts?.ready)await abortable(document.fonts.ready,signal);
        await Promise.all([...node.querySelectorAll('img')].map(image=>image.complete ? (image.naturalWidth?Promise.resolve():Promise.reject(new Error('An image inside this document could not be loaded. Please retry before adding notes.'))) : abortable(new Promise((resolve,reject)=>{
            image.onload=resolve;image.onerror=()=>reject(new Error('An image inside this document could not be loaded. Please retry before adding notes.'));
        }),signal)));
        checkAbort(signal);
        const height=Math.ceil(Math.max(node.scrollHeight,node.getBoundingClientRect().height));
        if(!height)throw new Error('The document preview could not be measured. Please retry.');
        return {...page,height:Math.max(160,height),needsMeasurement:false};
    } finally {node.remove();}
}

let pdfModulePromise;
const getPdfModule=()=>{
    if(!pdfModulePromise)pdfModulePromise=import('pdfjs-dist/legacy/build/pdf.mjs').catch(error=>{pdfModulePromise=null;throw error;});
    return pdfModulePromise;
};
async function loadPdf(lecture,options) {
    const {signal,onProgress}=options;
    const library=await abortable(getPdfModule(),signal);
    checkAbort(signal);
    const base=`${(process.env.PUBLIC_URL || '').replace(/\/$/,'')}/pdfjs/${PDF_VERSION}/`;
    library.GlobalWorkerOptions.workerSrc=base+'pdf.worker.min.mjs';
    const task=library.getDocument({url:lecture.url,cMapUrl:base+'cmaps/',cMapPacked:true,standardFontDataUrl:base+'standard_fonts/',wasmUrl:base+'wasm/',iccUrl:base+'iccs/',enableXfa:false});
    let destroyed=false;
    const destroy=()=>{
        if(destroyed)return Promise.resolve();destroyed=true;
        signal?.removeEventListener('abort',abort);
        return Promise.resolve(task.destroy()).catch(()=>{});
    };
    const abort=()=>{destroy();};
    signal?.addEventListener('abort',abort,{once:true});
    if(onProgress)task.onProgress=onProgress;
    try {
        const pdf=await abortable(task.promise,signal,destroy);
        if(!pdf.numPages)throw new Error('This PDF has no readable pages.');
        const labels=await pdf.getPageLabels().catch(()=>null);
        const pages=new Array(pdf.numPages);
        let cursor=0;
        const worker=async()=>{
            while(cursor<pdf.numPages) {
                const index=cursor++;
                checkAbort(signal);
                const page=await abortable(pdf.getPage(index+1),signal,destroy);
                const viewport=page.getViewport({scale:1});
                if(!(viewport.width>0 && viewport.height>0))throw new Error(`Page ${index+1} has invalid dimensions.`);
                pages[index]={key:`pdf-page-${index+1}`,index,label:labels?.[index] || `Page ${index+1}`,kind:'pdf',width:viewport.width,height:viewport.height};
                page.cleanup();
            }
        };
        await Promise.all(Array.from({length:Math.min(4,pdf.numPages)},worker));
        checkAbort(signal);
        return {kind:'pdf',title:lecture.title || 'Lecture PDF',sourceUrl:lecture.url,pages,destroy,getPdfPage:index=>{
            if(destroyed)return Promise.reject(abortError());
            if(!Number.isInteger(index) || index<0 || index>=pages.length)return Promise.reject(new Error('This PDF page is unavailable.'));
            return pdf.getPage(index+1);
        }};
    } catch(error) {
        await destroy();
        if(signal?.aborted || error.name==='AbortError')throw abortError();
        if(error.name==='PasswordException')throw new Error('This PDF is password-protected. Ask your tutor to upload an unlocked PDF for annotation.');
        throw new Error(`The PDF could not be opened. ${error.message || 'Check your connection and retry.'}`);
    }
}

export async function loadLectureMaterial(lecture,{signal,onProgress}={}) {
    checkAbort(signal);
    if(!lecture?.url || !safeUrl(lecture.url))throw new Error('The lecture material does not have a valid file link.');
    if(lecture.type==='pdf')return loadPdf(lecture,{signal,onProgress});
    if(lecture.type==='image') {
        const image=await readImage(lecture.url,signal);
        const width=Math.min(800,image.naturalWidth),height=width*image.naturalHeight/image.naturalWidth;
        return {kind:'image',title:lecture.title || 'Lecture image',sourceUrl:lecture.url,pages:[{key:'image-1',index:0,label:'Image',kind:'image',src:lecture.url,width,height}],destroy:()=>{}};
    }
    if(!['word','excel'].includes(lecture.type))throw new Error('This file type cannot be annotated here. Upload a PDF, image, Word document (.docx), or spreadsheet.');
    let name=lecture.fileName || lecture.url.split('?')[0];
    try {name=decodeURIComponent(name);}catch {}
    if(lecture.type==='word' && /\.doc$/i.test(name))throw new Error('Please save this older Word file as .docx or PDF and upload it again to annotate it.');
    const response=await fetch(lecture.url,{signal});
    if(!response.ok)throw new Error(`The lecture file could not be downloaded (${response.status}). Please retry.`);
    const arrayBuffer=await response.arrayBuffer();
    checkAbort(signal);
    let pages;
    if(lecture.type==='word') {
        const mammoth=await import('mammoth');
        const result=await mammoth.convertToHtml({arrayBuffer});
        const html=sanitizeMaterialHtml(result.value);
        if(!html.trim())throw new Error('This Word document has no readable content.');
        pages=[{key:'word-1',index:0,label:'Document',kind:'html',width:MATERIAL_HTML_WIDTH,height:null,needsMeasurement:true,html}];
    } else {
        const XLSX=await import('xlsx');
        const workbook=XLSX.read(arrayBuffer,{type:'array'});
        pages=workbook.SheetNames.map((name,index)=>({key:`sheet-${index+1}`,index,label:name,kind:'html',width:MATERIAL_HTML_WIDTH,height:null,needsMeasurement:true,html:sanitizeMaterialHtml(XLSX.utils.sheet_to_html(workbook.Sheets[name]))}));
        if(!pages.length)throw new Error('This spreadsheet has no readable sheets.');
    }
    checkAbort(signal);
    return {kind:lecture.type,title:lecture.title || 'Lecture document',sourceUrl:lecture.url,pages,destroy:()=>{}};
}
